/**
 * Where a compatibility redirect should land: the destination path plus the
 * caller's query string and hash, so bookmarks, shared links and Axon deep
 * links keep their filters when a page moves.
 *
 * Query params written into `to` (e.g. `?tab=tokenomics`) are the canonical
 * mapping and win over the caller's copy of the same key. When `to` carries no
 * query, the caller's string is passed through untouched rather than
 * re-encoded — URLSearchParams would turn `%20` into `+`, which pages that
 * decode location.search by hand read back as a literal plus.
 *
 * Kept free of react-router so it can be unit tested under CRA's Jest.
 *
 * @param {string} to  destination path, optionally with its own ?query and #hash
 * @param {{search?: string, hash?: string}} [location]  the current location
 * @returns {{pathname: string, search: string, hash: string}}
 */
export function buildRedirectTarget(to, { search = '', hash = '' } = {}) {
  const hashAt = to.indexOf('#');
  const pathAndQuery = hashAt === -1 ? to : to.slice(0, hashAt);
  const toHash = hashAt === -1 ? '' : to.slice(hashAt);
  const queryAt = pathAndQuery.indexOf('?');
  const pathname = queryAt === -1 ? pathAndQuery : pathAndQuery.slice(0, queryAt);
  const toQuery = queryAt === -1 ? '' : pathAndQuery.slice(queryAt + 1);

  let mergedSearch = search;
  if (toQuery) {
    const params = new URLSearchParams(search);
    new URLSearchParams(toQuery).forEach((value, key) => params.set(key, value));
    mergedSearch = `?${params.toString()}`;
  }

  return { pathname, search: mergedSearch, hash: toHash || hash };
}
